import { useCallback, useEffect, useMemo, useState } from "react";
import { BranchCode, GetUnemploymentRiskResponse, GetUpiRiskResponse, GetRgiRiskResponse, GetMriRiskResponse } from "api/risk";
import { PersonDto } from "api/contract";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";
import { useActorRegistration } from "./useActorRegistration";
import { Sizes } from "components/forms/NumberSelector";
import { useSubmitActorForm } from "features/new-actor/hooks/optim/useSubmitActorForm";
import { useNavigate } from "react-router-dom";
import { useSubscriptionProcessState } from "features/subscription-process/contexts/selectors/selectors";
import { SearchPersonResult } from "components/domain/SearchBar/types";
import { MRI_ROUTES } from "features/subscription-process/utils/mriRoutes";

export const useActorRegistrationBySearchBar = (branchCode: BranchCode) => {
  const { riskId, formikRef, isFormValid, selectedPerson, setSelectedPerson, ...rest } = useActorRegistration(branchCode)
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const navigate = useNavigate();

  const { riskData } = useSubscriptionProcessState()

  // Extract person from riskData based on type
  const person = useMemo(() => {
    if (!riskData) return undefined
    return (riskData as GetUnemploymentRiskResponse | GetUpiRiskResponse | GetRgiRiskResponse | GetMriRiskResponse).person
  }, [riskData])

  // State
  const [isAddFormActive, setIsAddFormActive] = useState(false);

  useEffect(() => {
    setSelectedPerson(person)
  }, [person, setSelectedPerson])

  const shouldShowSummary = useMemo(() => {
    return !!person
  }, [person]);


  const shouldShowSearchPage = useMemo(() => {
    return (!isAddFormActive && !selectedPerson)
  }, [isAddFormActive, selectedPerson]);

  const handleSelectPerson = useCallback(
    async (person: SearchPersonResult | null) => {
      if (!person) return;
      setSelectedPerson(person.data as PersonDto)
    },
    [setSelectedPerson]
  );

  const handleShowAddForm = useCallback(() => {
    setSelectedPerson(undefined)
    setIsAddFormActive(true);
  }, [setSelectedPerson]);

  const handleHideAddForm = useCallback(() => {
    setSelectedPerson(undefined)
    setIsAddFormActive(false);
  }, [setSelectedPerson]);

  // The MRI step registers the co-ownership representative, not an insured person:
  // same form and search, other wording. i18n keys, resolved by the consumers.
  const registrationWording = useMemo(() => {
    if (branchCode === BranchCode.Mri) {
      return {
        searchTitle: "ACTOR_FORM.REPRESENTATIVE.LABELS.WHICH_REPRESENTATIVE",
        searchPlaceholder: "ACTOR_FORM.REPRESENTATIVE.LABELS.SEARCH_REPRESENTATIVE",
        createLabel: "ACTOR_FORM.REPRESENTATIVE.LABELS.CREATE_REPRESENTATIVE",
        formTitleKeys: {
          create: "ACTOR_FORM.REPRESENTATIVE.LABELS.CO_OWNERSHIP_REPRESENTATIVE",
          edit: "ACTOR_FORM.REPRESENTATIVE.LABELS.EDIT_REPRESENTATIVE",
        },
      }
    }
    return {
      searchTitle: "ACTOR_FORM.LABELS.WHICH_INSURED_PERSON",
      searchPlaceholder: "ACTOR_FORM.SEARCH_BAR.LABELS.SEARCH_ONE_OF_INSURED_PERSON",
      createLabel: "ACTOR_FORM.LABELS.CREATE_NEW_INSURED_PERSON",
      formTitleKeys: undefined,
    }
  }, [branchCode])

  // Event handlers
  const handleSubmitClick = useCallback(() => {
    const formik = formikRef.current;
    if (!formik || !riskId) return;
    formikRef.current?.submitForm();
  }, [riskId, formikRef]);


  const stepPageConfig = useMemo(() => ({
    nextConfig: {
      label: t('COMMON.NEXT_STEP'),
      onClick: handleSubmitClick,
      disabled: !isFormValid,
    },
    prevConfig: {
      label: t('COMMON.CANCEL'),
      onClick: handleHideAddForm,
    },
    containerSize: Sizes["5xl"],
  }), [t, isFormValid, handleHideAddForm, handleSubmitClick]);

  const postSubmit = useCallback(async () => {
    if (riskId)
      switch (branchCode) {
        case BranchCode.UPI:
          navigate(`/simulation/inscription/cbno/biens/${riskId}`, { state: { branchCode } });
          break
        case BranchCode.Unemployment:
          navigate(`/simulation/profilage/chomage/${riskId}`, { state: { branchCode } });
          break
        case BranchCode.RGI:
          navigate(`/simulation/inscription/gli/biens/${riskId}`, { state: { branchCode } });
          break
        case BranchCode.Mri:
          navigate(`${MRI_ROUTES.copropertyRegistration}/${riskId}`, { state: { branchCode } });
          break
      }
  }, [branchCode, riskId, navigate])


  const { handleSubmit, editConfig } = useSubmitActorForm({
    person: selectedPerson,
    postSubmit,
    riskId,
    formikRef
  });

  return {
    ...rest,
    formikRef,
    handleSubmit,
    editConfig,
    shouldShowSearchPage,
    handleShowAddForm,
    handleSelectPerson,
    stepPageConfig,
    registrationWording,
    shouldShowSummary
  };
};
